import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...args) {
  return twMerge(clsx(args));
}

export const STATUS_LABELS = {
  new: "New",
  under_review: "Under Review",
  approved: "Approved",
  assigned: "Assigned",
  development: "Development",
  testing: "Testing",
  ready_for_client_review: "Ready for Review",
  completed: "Completed",
  closed: "Closed",
  waiting_for_client: "Waiting on Client",
  on_hold: "On Hold",
  rejected: "Rejected",
  duplicate: "Duplicate",
};

export const STATUS_TONES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  assigned: "bg-indigo-50 text-indigo-700 border-indigo-200",
  development: "bg-violet-50 text-violet-700 border-violet-200",
  testing: "bg-cyan-50 text-cyan-700 border-cyan-200",
  ready_for_client_review: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  waiting_for_client: "bg-amber-50 text-amber-700 border-amber-200",
  on_hold: "bg-slate-100 text-slate-700 border-slate-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  duplicate: "bg-slate-100 text-slate-700 border-slate-200",
};

export const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
};

export const PRIORITY_TONES = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  medium: "bg-blue-50 text-blue-700 border-blue-200",
  high: "bg-amber-50 text-amber-700 border-amber-200",
  urgent: "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
};

export const REQUEST_TYPES = [
  { value: "bug_report", label: "Bug Report" },
  { value: "change_request", label: "Change Request" },
  { value: "new_feature", label: "New Feature" },
  { value: "content_update", label: "Content Update" },
  { value: "data_update", label: "Data Update" },
  { value: "technical_support", label: "Technical Support" },
  { value: "server_issue", label: "Server Issue" },
  { value: "training_request", label: "Training Request" },
  { value: "consultation_request", label: "Consultation Request" },
];

export const PRIORITIES = ["low", "medium", "high", "critical"];
export const IMPACTS = ["minor", "major", "business_critical"];

export const ALL_STATUSES = Object.keys(STATUS_LABELS);

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  project_manager: "Project Manager",
  team_member: "Team Member",
  client_admin: "Client Admin",
  client_user: "Client User",
};

export const GLOBAL_ROLES = ["super_admin", "project_manager"];

// Roles that belong to exactly one client organisation.
export const CLIENT_ROLES = ["client_admin", "client_user"];

export const ROLE_DESCRIPTIONS = {
  super_admin: {
    blurb: "Full control of the workspace — settings, branding, master data, ticket statuses, and all users.",
    access: "Every client and project.",
  },
  project_manager: {
    blurb: "Runs delivery across all clients — manages tickets, assigns work, and manages team & client users.",
    access: "Every client and project.",
  },
  team_member: {
    blurb: "Works on assigned tickets, logs time, and collaborates with clients. Can't change priority or business impact.",
    access: "Only the clients and projects they're assigned to.",
  },
  client_admin: {
    blurb: "The client's administrator — raises and tracks tickets and manages their own organisation's users.",
    access: "Their organisation's projects.",
  },
  client_user: {
    blurb: "A client contact who raises tickets and follows their progress.",
    access: "Their organisation's projects.",
  },
};

// Staff roles create tickets only when the tenant enables it (Settings → Roles).
export function canCreateTickets(user) {
  if (["super_admin", "client_admin", "client_user"].includes(user?.role)) return true;
  if (["project_manager", "team_member"].includes(user?.role)) return !!user?.company?.staff_ticket_creation;
  return false;
}

export function roleOptionsFor(actorRole) {
  if (actorRole === "super_admin") return ["super_admin", "project_manager", "team_member", "client_admin", "client_user"];
  if (actorRole === "project_manager") return ["team_member", "client_admin", "client_user"];
  if (actorRole === "client_admin") return ["client_admin", "client_user"];
  return [];
}

export function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
}

export function formatBytes(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function isImageType(ct = "") {
  return ct.startsWith("image/");
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Human-readable sub-type list for a ticket: "ui issue, checkout flow" ("" when none).
// Falls back to the legacy single-value field for tickets not yet migrated.
export function subtypesText(t) {
  const list = t?.request_subtypes?.length ? t.request_subtypes : t?.request_subtype ? [t.request_subtype] : [];
  return list.map((s) => s.replace(/_/g, " ")).join(", ");
}

// Compact elapsed duration, up to two units: "<1m", "12m", "6h 20m", "5d 3h", "5d".
export function humanizeDuration(ms) {
  if (!ms || ms < 0 || Number.isNaN(ms)) return "";
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return "<1m";
  if (totalMin < 60) return `${totalMin}m`;
  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    const m = totalMin % 60;
    return m ? `${totalHr}h ${m}m` : `${totalHr}h`;
  }
  const d = Math.floor(totalHr / 24);
  const h = totalHr % 24;
  return h ? `${d}d ${h}h` : `${d}d`;
}

// Given an ISO due time (and optional ISO started time), returns null (no SLA)
// or { label, tone, ms } for display.
// tone: "rose" = overdue, "amber" = due soon (25% of budget, min 15m), "slate" = ok.
export function slaState(dueIso, startedIso) {
  if (!dueIso) return null;
  const ms = new Date(dueIso) - Date.now();
  if (ms <= 0) return { label: `overdue ${humanizeDuration(-ms)}`, tone: "rose", ms };
  const total = startedIso ? (new Date(dueIso) - new Date(startedIso)) : 0;
  const soonThreshold = total > 0 ? Math.max(15 * 60 * 1000, 0.25 * total) : 15 * 60 * 1000;
  return { label: `${humanizeDuration(ms)} left`, tone: ms < soonThreshold ? "amber" : "slate", ms };
}
